import React, { useMemo, useState } from 'react'
import { FiChevronUp, FiChevronDown, FiExternalLink } from 'react-icons/fi'
import { useTheme } from 'styled-components'

import { SearchField } from '../..'
import { Button } from '../../../../../common/components'
import { RankingReport } from '../../../hooks/useReportsByTeacher'

import { Container, Header, Label, Table } from '../styles'

interface ReportProps {
  data: RankingReport[]
}

export const ReadingRankingReport: React.FC<ReportProps> = ({ data }) => {
  const theme = useTheme()

  const [search, setSearch] = useState<string>('')
  const [column, setColumn] = useState<keyof RankingReport | null>(null)
  const [inverse, setInverse] = useState<boolean>(false)

  const handleSort = (value: keyof RankingReport) => {
    if (value === column) {
      setInverse(current => !current)
    } else {
      setColumn(value)
      setInverse(false)
    }
  }

  const handleExport = () => {
    const html = document.getElementById('excel-export--reading-ranking')
    const table = html?.outerHTML
    window.open(
      'data:application/vnd.ms-excel,' + encodeURIComponent(table as string)
    )
  }

  const filtered = useMemo(() => {
    if (!search) return data
    const arr =
      data.filter(
        item =>
          item.bookName
            .toLocaleLowerCase()
            .indexOf(search.toLocaleLowerCase()) !== -1
      ) || []
    return arr
  }, [search, data])

  const sorted = useMemo(() => {
    if (!column) return filtered
    const arr = filtered.sort((a, b) => {
      if (a[column] < b[column]) {
        if (inverse) {
          return 1
        }
        return -1
      }

      if (a[column] > b[column]) {
        if (inverse) {
          return -1
        }
        return 1
      }

      return 0
    })
    return arr
  }, [column, inverse, search, data])

  const totalAccessNumber = useMemo(() => {
    return filtered.reduce((sum, item) => sum + item.amount, 0)
  }, [data, search, filtered])

  return (
    <Container>
      <Header>
        <SearchField
          placeholder="Filtrar por livro"
          value={search}
          onChange={setSearch}
        />
        <Button variant="primary" onClick={handleExport}>
          <FiExternalLink
            color={theme.palette.buttons.primary.default.label}
            size={20}
          />
          Exportar
        </Button>
      </Header>
      <Label>
        Clique no cabeçalho da coluna desejada para ordenar a tabela
      </Label>
      <Table>
        <table id="excel-export--reading-ranking">
          <thead>
            <tr>
              <th onClick={() => handleSort('bookName')}>
                <span>
                  Livro{' '}
                  {column === 'bookName' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
              <th onClick={() => handleSort('amount')}>
                <span>
                  Quantidade de leituras{' '}
                  {column === 'amount' &&
                    (inverse ? (
                      <FiChevronUp
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ) : (
                      <FiChevronDown
                        size={18}
                        color={theme.palette.table.head.label}
                        style={{ marginLeft: '10px' }}
                      />
                    ))}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((item, index) => (
              <tr key={index}>
                <td>
                  <span>{item.bookName}</span>
                </td>
                <td>
                  <span>{item.amount}</span>
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>
                <span>Total geral</span>
              </td>
              <td>
                <span>{totalAccessNumber}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </Table>
    </Container>
  )
}
